import { CodingLayout } from "@/components/CodingLayout";
import { FilesystemPanel } from "@/components/agent-fs/FilesystemPanel";
import { Thread } from "@/components/assistant-ui/thread";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useOnboarding } from "@/features/onboarding/OnboardingContext";
import { getOnboardingPageContext } from "@/features/onboarding/chat-onboarding";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { CommunicationService } from "@/gen/omnios/v1/communication_connect";
import { ConversationKind, SenderKind } from "@/gen/omnios/v1/communication_pb";
import { PartyService } from "@/gen/omnios/v1/party_connect";
import { PartyKind } from "@/gen/omnios/v1/party_pb";
import { useAgentStore } from "@/lib/agent";
import { EntityContextProvider } from "@/lib/entity-context";
import type { EntityRef } from "@/lib/entity-context";
import { transport } from "@/lib/grpc";
import { useAgentChat } from "@/lib/use-agent-chat";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { AssistantRuntimeProvider } from "@assistant-ui/react";
import type { ThreadMessageLike } from "@assistant-ui/react";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import {
	Outlet,
	createFileRoute,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	ChevronDown,
	Pencil,
	PenLine,
	Search,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/assistant")({
	component: AssistantLayout,
});

const commClient = createClient(CommunicationService, transport);
const partyClient = createClient(PartyService, transport);
const aiClient = createClient(AIService, transport);

// ── Hooks ──

function useAgentPartyId() {
	const { agentPartyId, setAgentPartyId } = useAgentStore();
	useEffect(() => {
		if (agentPartyId) return;
		(async () => {
			try {
				const res = await partyClient.listParties({
					kind: PartyKind.AGENT,
					page: { pageSize: 1, pageToken: "" },
				});
				if (res.parties.length > 0) setAgentPartyId(res.parties[0].id);
			} catch {}
		})();
	}, [agentPartyId, setAgentPartyId]);
	return agentPartyId;
}

function useThreads() {
	const [threads, setThreads] = useState<
		{ id: string; title: string; updatedAt?: Date }[]
	>([]);
	const [loading, setLoading] = useState(true);
	const fetch = useCallback(async () => {
		try {
			const res = await commClient.listConversations({
				kind: ConversationKind.AGENT_THREAD,
				page: { pageSize: 100, pageToken: "" },
			});
			setThreads(
				res.conversations.map((c) => {
					// Use subject, or fall back to last message preview
					let title = c.subject || "";
					if (!title && c.lastMessage) {
						const text = c.lastMessage.content
							?.map((ct: any) => ct.content?.value?.text ?? "")
							.join("")
							.trim();
						if (text) title = text.slice(0, 60);
					}
					return {
						id: c.id,
						title,
						updatedAt: c.updatedAt?.toDate(),
					};
				}),
			);
		} catch {}
		setLoading(false);
	}, []);
	useEffect(() => {
		fetch();
	}, [fetch]);
	return { threads, fetchThreads: fetch, threadsLoading: loading };
}

async function loadMessages(id: string): Promise<ThreadMessageLike[]> {
	const res = await commClient.listMessages({
		conversationId: id,
		page: { pageSize: 200, pageToken: "" },
	});
	return res.messages.map((m) => {
		const role =
			m.senderKind === SenderKind.AI
				? ("assistant" as const)
				: ("user" as const);
		const text = m.content
			.map((c) => (c.content.case === "text" ? c.content.value.text : ""))
			.join("");
		return {
			id: m.id,
			role,
			content: text,
			createdAt: m.createdAt?.toDate(),
			...(role === "assistant"
				? { status: { type: "complete" as const, reason: "stop" as const } }
				: {}),
		};
	});
}

// ── Thread grouping by date ──

function groupThreadsByDate(
	threads: { id: string; title: string; updatedAt?: Date }[],
	t: (key: string) => string,
) {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today.getTime() - 86400000);
	const weekAgo = new Date(today.getTime() - 7 * 86400000);

	const groups: { label: string; items: typeof threads }[] = [];
	const todayItems: typeof threads = [];
	const yesterdayItems: typeof threads = [];
	const weekItems: typeof threads = [];
	const olderItems: typeof threads = [];

	for (const t of threads) {
		const d = t.updatedAt;
		if (!d || d >= today) todayItems.push(t);
		else if (d >= yesterday) yesterdayItems.push(t);
		else if (d >= weekAgo) weekItems.push(t);
		else olderItems.push(t);
	}

	if (todayItems.length) groups.push({ label: t("today"), items: todayItems });
	if (yesterdayItems.length)
		groups.push({ label: t("yesterday"), items: yesterdayItems });
	if (weekItems.length) groups.push({ label: t("this_week"), items: weekItems });
	if (olderItems.length) groups.push({ label: t("older"), items: olderItems });

	return groups;
}

// ── Sidebar ──

function Sidebar({
	threads,
	loading,
	activeId,
	onSelect,
	onNew,
	onDelete,
	onRename,
}: {
	threads: { id: string; title: string; updatedAt?: Date }[];
	loading: boolean;
	activeId: string | null;
	onSelect: (id: string) => void;
	onNew: () => void;
	onDelete: (id: string) => void;
	onRename: (id: string, title: string) => void;
}) {
	const { t } = useTranslation("assistant");
	const [search, setSearch] = useState("");
	const [editId, setEditId] = useState<string | null>(null);
	const [editTitle, setEditTitle] = useState("");
	const [searchResults, setSearchResults] = useState<
		{ conversationId: string; text: string }[]
	>([]);

	useEffect(() => {
		if (search.length < 2) {
			setSearchResults([]);
			return;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			try {
				const res = await commClient.searchMessages({
					query: search,
					limit: 10,
				});
				if (!cancelled)
					setSearchResults(
						res.hits.map((h) => ({
							conversationId: h.conversationId,
							text: h.text,
						})),
					);
			} catch {
				if (!cancelled) setSearchResults([]);
			}
		}, 300);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [search]);

	const showSearch = search.length >= 2 && searchResults.length > 0;
	const groups = useMemo(() => groupThreadsByDate(threads, t), [threads, t]);

	const { data: profiles } = useQuery({
		queryKey: ["agent-profiles"],
		queryFn: async () => {
			const resp = await aiClient.listAgentProfiles({
				includeSystemTemplates: true,
			});
			return resp.profiles;
		},
	});

	const searchRef = useRef<HTMLInputElement>(null);

	// "/" to focus search
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (
				e.key === "/" &&
				!e.metaKey &&
				!e.ctrlKey &&
				document.activeElement?.tagName !== "INPUT" &&
				document.activeElement?.tagName !== "TEXTAREA"
			) {
				e.preventDefault();
				searchRef.current?.focus();
			}
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, []);

	return (
		<aside className="w-64 shrink-0 border-r flex flex-col overflow-hidden">
			{/* New session button + profile picker */}
			<div className="p-2 shrink-0 space-y-1.5">
				<div className="flex">
					<Button
						className="flex-1 justify-start gap-2 rounded-r-none"
						onClick={onNew}
					>
						<PenLine className="size-4" />
						{t("new_dialog")}
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button className="rounded-l-none border-l border-primary-foreground/20 px-2">
								<ChevronDown className="size-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start" className="w-56">
							{profiles?.map((p) => (
								<DropdownMenuItem
									key={p.id}
									onSelect={() => {
										useAgentStore.getState().setProfileId(p.id);
										onNew();
									}}
								>
										<span className="truncate">{p.name}</span>
										{p.isTemplate && (
											<span className="ml-auto text-[10px] text-muted-foreground">
												{t("template")}
											</span>
										)}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{/* Search */}
				<div className="relative">
					<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/40" />
					<Input
						ref={searchRef}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={t("search_placeholder")}
						className="w-full rounded-lg pl-8 pr-8 py-1.5 text-sm placeholder:text-muted-foreground/50"
					/>
					<kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] text-muted-foreground/30 font-mono">
						/
					</kbd>
				</div>
			</div>

			{/* Thread list */}
			<div className="flex-1 overflow-y-auto">
				{showSearch ? (
					<div className="px-2 space-y-px">
						{searchResults.map((r) => (
							<Button
								key={r.conversationId}
								variant="ghost"
								className="w-full justify-start rounded-lg px-3 py-2 h-auto"
								onClick={() => onSelect(r.conversationId)}
							>
								<p className="text-xs text-muted-foreground truncate">
									{r.text.slice(0, 80)}
								</p>
							</Button>
						))}
					</div>
				) : loading ? (
					<div className="px-2 space-y-1 pt-2">
						{Array.from({ length: 6 }).map((_, i) => (
							<div
								key={i}
								className="h-9 rounded-lg bg-muted/40 animate-pulse"
								style={{ animationDelay: `${i * 75}ms` }}
							/>
						))}
					</div>
				) : (
					groups.map((group) => (
						<div key={group.label} className="mb-1">
							<p className="px-4 pt-3 pb-1 text-[11px] font-medium text-muted-foreground/50 uppercase tracking-wider">
								{group.label}
							</p>
							<div className="px-2 space-y-px">
								{group.items.map((thread) => (
									<ThreadItem
										key={thread.id}
										thread={thread}
										isActive={thread.id === activeId}
										isEditing={editId === thread.id}
										editTitle={editTitle}
										onSelect={() => onSelect(thread.id)}
										onStartEdit={() => {
											setEditId(thread.id);
											setEditTitle(thread.title || "");
										}}
										onEditChange={setEditTitle}
										onEditSubmit={() => {
											if (editTitle.trim())
												onRename(thread.id, editTitle.trim());
											setEditId(null);
										}}
										onEditCancel={() => setEditId(null)}
											onDelete={() => onDelete(thread.id)}
											newDialogLabel={t("new_dialog")}
											renameLabel={t("rename_thread")}
											deleteLabel={t("delete_thread")}
											deleteTitle={t("delete_thread_title")}
											deleteDescription={t("delete_thread_description")}
											cancelLabel={t("common:cancel")}
											confirmDeleteLabel={t("common:delete")}
										/>
									))}
							</div>
						</div>
					))
				)}
			</div>
		</aside>
	);
}

// ── Thread item ──

function ThreadItem({
	thread,
	isActive,
	isEditing,
	editTitle,
	onSelect,
	onStartEdit,
	onEditChange,
	onEditSubmit,
	onEditCancel,
	onDelete,
	newDialogLabel,
	renameLabel,
	deleteLabel,
	deleteTitle,
	deleteDescription,
	cancelLabel,
	confirmDeleteLabel,
}: {
	thread: { id: string; title: string };
	isActive: boolean;
	isEditing: boolean;
	editTitle: string;
	onSelect: () => void;
	onStartEdit: () => void;
	onEditChange: (v: string) => void;
	onEditSubmit: () => void;
	onEditCancel: () => void;
	onDelete: () => void;
	newDialogLabel: string;
	renameLabel: string;
	deleteLabel: string;
	deleteTitle: string;
	deleteDescription: string;
	cancelLabel: string;
	confirmDeleteLabel: string;
}) {
	const [deleteOpen, setDeleteOpen] = useState(false);

	if (isEditing) {
		return (
			<div className="rounded-lg bg-accent px-3 py-2">
				<Input
					value={editTitle}
					onChange={(e) => onEditChange(e.target.value)}
					className="w-full bg-transparent text-sm border-0 shadow-none focus-visible:ring-0 h-auto p-0"
					autoFocus
					onBlur={onEditSubmit}
					onKeyDown={(e) => {
						if (e.key === "Enter") onEditSubmit();
						if (e.key === "Escape") onEditCancel();
					}}
				/>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"group relative rounded-lg px-3 py-2 cursor-pointer transition-colors",
				isActive ? "bg-accent text-accent-foreground" : "hover:bg-muted/70",
			)}
			onClick={onSelect}
			onDoubleClick={onStartEdit}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter") onSelect();
			}}
		>
			<p className="text-sm truncate pr-6">{thread.title || newDialogLabel}</p>

			{/* Actions on hover */}
			<div
				className={cn(
					"absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5",
					"opacity-0 group-hover:opacity-100 transition-opacity",
				)}
				onClick={(e) => e.stopPropagation()}
			>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-muted-foreground"
						onClick={onStartEdit}
						aria-label={renameLabel}
						title={renameLabel}
					>
						<Pencil className="size-3.5" />
					</Button>
					<AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
						<AlertDialogTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-6 w-6 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
								aria-label={deleteLabel}
								title={deleteLabel}
							>
								<Trash2 className="size-3.5" />
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>{deleteTitle}</AlertDialogTitle>
								<AlertDialogDescription>
									{deleteDescription}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
								<AlertDialogAction
									onClick={onDelete}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									{confirmDeleteLabel}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			</div>
		);
	}

function AssistantEmptyState({ onNew }: { onNew: () => void }) {
	const { t } = useTranslation("assistant");

	return (
		<div className="flex h-full flex-1 items-center justify-center p-8">
			<div className="max-w-sm space-y-4 text-center">
				<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
					<PenLine className="h-5 w-5 text-primary" />
				</div>
				<div className="space-y-1">
					<h2 className="text-xl font-semibold">{t("empty_title")}</h2>
					<p className="text-sm text-muted-foreground">
						{t("empty_description")}
					</p>
				</div>
				<Button onClick={onNew} className="gap-2">
					<PenLine className="h-4 w-4" />
					{t("new_dialog")}
				</Button>
			</div>
		</div>
	);
}

// ── Chat panel ──

function ChatPanel({
	threadId,
	agentPartyId,
	onResponseComplete,
}: {
	threadId: string | null;
	agentPartyId: string;
	onResponseComplete?: () => void;
}) {
	const { isActive, mode, currentStep, chatThreadId } = useOnboarding();
	const isOnboardingThread =
		isActive && mode === "chat" && !!chatThreadId && threadId === chatThreadId;
	const pageContext = useMemo(
		() =>
			isOnboardingThread ? getOnboardingPageContext(currentStep) : undefined,
		[isOnboardingThread, currentStep],
	);

	// Entity context for @-mentions
	const [entities, setEntities] = useState<EntityRef[]>([]);
	const entitiesRef = useRef<EntityRef[]>(entities);
	entitiesRef.current = entities;
	const addEntityContext = useCallback((e: EntityRef) => {
		setEntities((prev) => {
			if (
				prev.some(
					(x) => x.entityId === e.entityId && x.entityType === e.entityType,
				)
			)
				return prev;
			return [...prev, e];
		});
	}, []);
	const removeEntityContext = useCallback(
		(entityType: string, entityId: string) => {
			setEntities((prev) =>
				prev.filter(
					(x) => !(x.entityType === entityType && x.entityId === entityId),
				),
			);
		},
		[],
	);
	const clearEntities = useCallback(() => setEntities([]), []);

	const { runtime, setMessages, isRunning } = useAgentChat({
		agentPartyId,
		conversationId: threadId ?? "",
		pageContext,
		entitiesRef,
		clearEntities,
	});

	// Load initial messages for thread
	useEffect(() => {
		if (!threadId) return;
		let cancelled = false;
		loadMessages(threadId).then(
			(msgs) => !cancelled && setMessages(msgs),
			() => {},
		);
		return () => {
			cancelled = true;
		};
	}, [threadId, setMessages]);

	// Refresh sidebar after agent finishes responding
	const prevRunningRef = useRef(false);
	useEffect(() => {
		if (prevRunningRef.current && !isRunning) {
			onResponseComplete?.();
		}
		prevRunningRef.current = isRunning;
	}, [isRunning, onResponseComplete]);

	return (
		<EntityContextProvider
			value={{ addEntityContext, removeEntityContext, entities }}
		>
			<AssistantRuntimeProvider runtime={runtime}>
				<Thread />
			</AssistantRuntimeProvider>
		</EntityContextProvider>
	);
}

// ── Main layout ──

function AssistantLayout() {
	const navigate = useNavigate();
	const params = useParams({ strict: false }) as { threadId?: string };
	const [activeId, setActiveId] = useState<string | null>(
		params.threadId ?? null,
	);

	useEffect(() => {
		setActiveId(params.threadId ?? null);
	}, [params.threadId]);

	const agentPartyId = useAgentPartyId();
	const { threads, fetchThreads, threadsLoading } = useThreads();
	const { t: ta } = useTranslation("assistant");
	useBreadcrumbs([{ label: ta("title") }]);

	const createThread = useCallback(async () => {
		try {
			const conv = await commClient.createConversation({
				subject: "",
				kind: ConversationKind.AGENT_THREAD,
			});
			setActiveId(conv.id);
			await fetchThreads();
			navigate({ to: "/assistant/$threadId", params: { threadId: conv.id } });
		} catch {
			toast.error(ta("create_thread_error"));
		}
	}, [fetchThreads, navigate, ta]);

	const layoutMode = useAgentStore((s) => s.layoutMode);
	const location = useLocation();
	const isSubRoute =
		location.pathname.startsWith("/assistant/sessions") ||
		location.pathname.startsWith("/assistant/memory");

	const chatPanel = activeId ? (
		<ChatPanel
			key={activeId}
			threadId={activeId}
			agentPartyId={agentPartyId}
			onResponseComplete={fetchThreads}
		/>
	) : (
		<AssistantEmptyState onNew={() => void createThread()} />
	);

	const sidebarEl = (
		<Sidebar
			threads={threads}
			loading={threadsLoading}
			activeId={activeId}
			onSelect={(id) => {
				setActiveId(id);
				navigate({ to: "/assistant/$threadId", params: { threadId: id } });
			}}
			onNew={() => {
				void createThread();
			}}
			onDelete={async (id) => {
				try {
					await commClient.deleteConversation({ id });
					await fetchThreads();
					if (activeId === id) {
						setActiveId(null);
						navigate({ to: "/assistant" });
					}
				} catch {
					toast.error(ta("delete_thread_error"));
				}
			}}
			onRename={async (threadId, title) => {
				try {
					await commClient.updateConversation({ id: threadId, subject: title });
					await fetchThreads();
				} catch {
					toast.error(ta("rename_thread_error"));
				}
			}}
		/>
	);

	const fullHeight = (
		<div className="flex h-full overflow-hidden">
			{sidebarEl}
			{isSubRoute ? (
				<div className="flex-1 overflow-y-auto p-6">
					<Outlet />
				</div>
			) : layoutMode === "split" ? (
				<>
					<div className="flex-1 overflow-hidden">
						<CodingLayout chatSlot={chatPanel} />
					</div>
					<Outlet />
				</>
			) : (
				<>
					<div className="flex-1 overflow-hidden flex flex-col">
						<div className="flex-1 overflow-hidden min-h-0">{chatPanel}</div>
						<FilesystemPanel />
					</div>
					<Outlet />
				</>
			)}
		</div>
	);

	return fullHeight;
}
